/* eslint-disable consistent-return */
import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { makeStyles } from '@material-ui/core/styles';
import { Button, MuiThemeProvider, Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { axuminTheme, poslumaTheme, darkGrey, poslumaDarkGrey, white } from '../../../../app/theme';
import { TRAINING_NAVIGATION, BASE_ROUTE } from '../../../../constants/trainingNav.constants';
import LEARNING_CHECK from '../../../../constants/learningCheck.constants';
import CASE_STUDY from '../../../../constants/caseStudy.constants';
// import FINDINGS from '../../../../constants/findings.constants';
import AppState from '../../../../models/redux/state/app.state';
import history from '../../../../services/browserHistory';
import { getTrainingAPIcall, patchTrainingAPIcall } from '../../../../redux/actions/training/training.actions';
import { getStatusesAPIcall } from '../../../../redux/actions/enums/enums.actions';
import AxuminNavBar from '../../../micro/BrandNavBar/AxuminNavBar';
import PoslumaNavBar from '../../../micro/BrandNavBar/PoslumaNavBar';
import AxuminTrainingNavBar from '../../../micro/TrainingNavBar/AxuminTrainingNavBar';
import PoslumaTrainingNavBar from '../../../micro/TrainingNavBar/PoslumaTrainingNavBar';
import AxuminIndication from '../../../micro/Indication/AxuminIndication';
import PoslumaIndication from '../../../micro/Indication/PoslumaIndication';
import TrainingFooter from '../../../micro/TrainingFooter/TrainingFooter';
import ExternalLinkModal from '../../../micro/ExternalLinkModal/ExternalLinkModal';
import MainPageAxumin from './MainPageAxumin';
import MainPagePosluma from './MainPagePosluma';

const useStyles = makeStyles(() => ({
  axuminGrayBox: {
    backgroundColor: darkGrey,
    paddingTop: 49,
    paddingBottom: 50,
    paddingLeft: 64,
    paddingRight: 64,
  },
  poslumaGrayBox: {
    backgroundColor: poslumaDarkGrey,
    paddingTop: 49,
    paddingBottom: 50,
    paddingLeft: 64,
    paddingRight: 64,
  },
  content: {
    maxWidth: 1242,
    margin: 'auto',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  buttons: {
    marginTop: 16,
    marginLeft: 32,
    width: 132,
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 24,
  },
  footnotes: {
    marginTop: 32,
  },
  footnote: {
    fontSize: 14,
    letterSpacing: 0,
    lineHeight: 1.42,
    color: white,
    '& p': {
      margin: 0,
    },
    '& a': {
      color: 'inherit',
      textDecoration: 'none',
    },
    '& ol': {
      paddingLeft: 16,
      margin: 0,
    },
    '& sup': {
      verticalAlign: 'top',
      fontSize: 'smaller',
    },
  },
  urlFootnote: {
    display: 'inline',
    fontSize: 14,
    letterSpacing: 0,
    lineHeight: 1.42,
    color: white,
    '& p': {
      margin: 0,
      display: 'inline',
    },
  },
  url: {
    display: 'inline',
    fontSize: 14,
    letterSpacing: 0,
    lineHeight: 1.42,
    color: white,
    cursor: 'pointer',
  },
}));

type TrainingPageProps = {
  trainingId: number;
  module: string;
  page: number;
};

const TrainingPage = ({ trainingId, module, page }: TrainingPageProps): JSX.Element => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = trainingId === 1 ? axuminTheme : poslumaTheme;
  const user = useSelector((state: AppState) => state.auth.user);
  const statuses = useSelector((state: AppState) => state.enums.statuses);
  const userTrainings = useSelector((state: AppState) => state.training.current);
  const [timeEnteredPage, setTimeEnteredPage] = useState<number>();
  const [modalOpen, setModalOpen] = useState(false);
  const navigationData = TRAINING_NAVIGATION[module];
  const { windowTitle, nextRoute, previousRoute, title, footnotes = [] } = navigationData[page.toString()];

  const getCurrentTraining = () => {
    if (userTrainings) {
      const match = userTrainings.trainings.find((userTraining) => module.includes(userTraining.training.name));
      return match;
    }
    return null;
  };

  useEffect(() => {
    // Is this still the proper condition?
    if (!userTrainings && user) {
      dispatch(getTrainingAPIcall(trainingId.toString()));
    }
    if (!statuses) {
      dispatch(getStatusesAPIcall());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = windowTitle;
  }, [windowTitle]);

  const [nextDisabled, setNextDisabled] = useState(false);
  const [nextDisabledCS, setNextDisabledCS] = useState(false);
  // const [nextDisabledFindings, setNextDisabledFindings] = useState(false);

  useEffect(() => {
    const now = new Date().getTime();
    setTimeEnteredPage(now);

    const questionsArray = LEARNING_CHECK[module][page.toString()] || [];
    setNextDisabled(questionsArray.length > 0);

    const caseStudyData = CASE_STUDY[module][page.toString()] || {};
    setNextDisabledCS(Object.keys(caseStudyData).length > 0);

    // const findingsData = FINDINGS[module][page.toString()] || {};
    // setNextDisabledFindings(Object.keys(findingsData).length > 0);
  }, [module, page]);

  const baseRoute = BASE_ROUTE[module];
  const handleClickNext = () => {
    const currentTraining = getCurrentTraining();
    const now = new Date().getTime();
    let completedTrack = false;
    let status;
    let completed;
    if (page === 1) {
      status = 3;
    }
    if (nextRoute === '') {
      status = 4;
      completed = new Date().toISOString();
    }
    if (timeEnteredPage && currentTraining && !currentTraining.dateCompleted) {
      const diff = now - timeEnteredPage;
      const minutes = Math.floor(diff / 60000) + currentTraining.timeSpent;
      dispatch(
        patchTrainingAPIcall(
          currentTraining.url,
          nextRoute === '' ? page : page + 1,
          minutes,
          status,
          completed?.toString(),
        ),
      );
    }
    if (userTrainings && currentTraining) {
      if (page === 1 && currentTraining.training.name === 'Interpretation') {
        userTrainings.trainings.forEach((training) => {
          if (training.training.name === 'Acquisition' && training.status.name === 'Completed') {
            const acquisitionCompletedRoute = '/qualityimage';
            history.push(`${baseRoute}${acquisitionCompletedRoute}`);
            dispatch(
              patchTrainingAPIcall(currentTraining.url, nextRoute === acquisitionCompletedRoute ? page + 10 : page + 1),
            );
            completedTrack = true;
          }
        });
      }
    }
    if (nextRoute === '') {
      if (module === 'axuminInterpretation') {
        history.push('/axumin/interpretation/thankyou');
      }
      if (module === 'axuminAcquisition') {
        history.push('/axumin/acquisition/thankyou');
      }
      if (module === 'poslumaAcquisition') {
        history.push('/posluma/acquisition/thankyou');
      }
      if (module === 'poslumaInterpretation') {
        history.push('/posluma/interpretation/thankyou');
      }
    }
    if (!completedTrack && nextRoute) {
      history.push(`${baseRoute}${nextRoute}`);
    }
  };

  const handleClickPrevious = () => {
    const currentTraining = getCurrentTraining();
    const now = new Date().getTime();
    if (timeEnteredPage && currentTraining && !currentTraining.dateCompleted) {
      const diff = now - timeEnteredPage;
      const minutes = Math.floor(diff / 60000) + currentTraining.timeSpent;
      dispatch(patchTrainingAPIcall(currentTraining.url, page === 1 ? page : page - 1, minutes));
    }
    history.push(`${baseRoute}${previousRoute}`);
  };

  const parseForExternalLinks = (note: string) => {
    if (note.indexOf('<a') >= 0) {
      const start = note.indexOf('<a');
      const end = note.indexOf('</a>') + 4;
      const href = note.split('href="')[1].split('"')[0];
      let number = '';
      let firstString = note.substring(0, start);
      const split = note.substring(0, start).split('. ')[0];
      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(Number(split))) {
        number = `${split}. `;
        firstString = firstString.substring(split.length + 2);
      }
      return (
        <span className={classes.urlFootnote}>
          <span>{number}</span>
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>{firstString}</ReactMarkdown>
          <Typography className={classes.url} onClick={() => setModalOpen(true)}>
            {href}
          </Typography>
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>{note.substring(end)}</ReactMarkdown>
          <ExternalLinkModal url={href} isOpen={modalOpen} handleModalOpen={(value) => setModalOpen(value)} />
        </span>
      );
    }
    return <ReactMarkdown rehypePlugins={[rehypeRaw]}>{note}</ReactMarkdown>;
  };

  return (
    <MuiThemeProvider theme={theme}>
      {/* Nav Bar that changes based on the training */}
      {trainingId && trainingId === 1 ? <AxuminTrainingNavBar /> : <PoslumaTrainingNavBar />}
      {trainingId && trainingId === 1 ? (
        <AxuminNavBar section={module === 'axuminInterpretation' ? 'Image Interpretation' : 'Image Acquisition'} />
      ) : (
        <PoslumaNavBar section={module === 'poslumaInterpretation' ? 'Image Interpretation' : 'Image Acquisition'} />
      )}
      <div className={trainingId === 1 ? classes.axuminGrayBox : classes.poslumaGrayBox}>
        <div className={classes.content}>
          <Typography variant="h1">{title}</Typography>

          {/* We call the proper component based on the page we are on */}
          {trainingId === 1 && (
            <MainPageAxumin
              module={module}
              page={page}
              setNextDisabled={setNextDisabled}
              setNextDisabledCS={setNextDisabledCS}
              // setNextDisabledFindings={setNextDisabledFindings}
            />
          )}
          {trainingId === 2 && (
            <MainPagePosluma
              module={module}
              page={page}
              setNextDisabled={setNextDisabled}
              setNextDisabledCS={setNextDisabledCS}
              // setNextDisabledFindings={setNextDisabledFindings}
            />
          )}

          <div className={classes.buttonRow}>
            <Button variant="contained" color="primary" className={classes.buttons} onClick={handleClickPrevious}>
              PREVIOUS
            </Button>
            <Button
              disabled={(nextDisabled || nextDisabledCS) && !getCurrentTraining()?.dateCompleted}
              variant="contained"
              color="primary"
              className={classes.buttons}
              onClick={handleClickNext}
            >
              NEXT
            </Button>
          </div>
        </div>
        {footnotes.length > 0 ? (
          <div className={classes.footnotes}>
            {footnotes.map((note) => (
              <div key={note} className={classes.footnote}>
                {parseForExternalLinks(note)}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* Collapsed Indication */}
      {trainingId && trainingId === 1 ? <AxuminIndication /> : <PoslumaIndication />}
      {/* Off-White Footer */}
      <TrainingFooter />
    </MuiThemeProvider>
  );
};

export default TrainingPage;
